import React from "react";
import { COLORS, RADII, SHADOWS, SPACING } from "@fanduel/formation-theming";
import {
  BodyText,
  Button,
  Flex,
  HeaderText
} from "@fanduel/formation-react-components";
import {
  AlertProps,
  AlertOverlayProps,
  AlertTitleProps,
  AlertDescriptionProps,
  AlertActionsProps,
  AlertButtonProps
} from "./types";

const AlertRoot = ({ children, ...rest }: AlertProps) => (
  <AlertOverlay {...rest}>
    <Flex
      aria-modal="true"
      bg={COLORS.backgroundSurface}
      borderRadius={RADII.borderRadius020}
      boxShadow={SHADOWS.elevationsBottomLow}
      direction="column"
      gap={SPACING.space4}
      maxWidth={300}
      p={SPACING.space4}
      role="alertdialog"
      onClick={(e) => e.stopPropagation()}
      {...rest}
    >
      {children}
    </Flex>
  </AlertOverlay>
);
AlertRoot.displayName = "Alert";

const AlertOverlay = ({ children, onDismiss, ...rest }: AlertOverlayProps) => (
  <Flex
    align="center"
    bg={COLORS.coreBlackTint070}
    justify="center"
    position="fixed"
    top={0}
    left={0}
    right={0}
    bottom={0}
    zIndex={999}
    onClick={onDismiss}
    {...rest}
  >
    {children}
  </Flex>
);
AlertOverlay.displayName = "Alert.Overlay";

const AlertTitle = ({ children, ...rest }: AlertTitleProps) => (
  <HeaderText
    color={COLORS.contentStrong}
    size="lg"
    textAlign="center"
    {...rest}
  >
    {children}
  </HeaderText>
);
AlertTitle.displayName = "Alert.Title";

const AlertDescription = ({ children, ...rest }: AlertDescriptionProps) => (
  <BodyText color={COLORS.contentDefault} textAlign="center" {...rest}>
    {children}
  </BodyText>
);
AlertDescription.displayName = "Alert.Description";

const AlertActions = ({ children, ...rest }: AlertActionsProps) => (
  <Flex direction="column" gap={SPACING.space2} {...rest}>
    {children}
  </Flex>
);
AlertActions.displayName = "Alert.Actions";

const AlertButton = ({ label, variant, ...rest }: AlertButtonProps) => (
  <Button fill variant={variant} label={label} {...rest} />
);
AlertButton.displayName = "Alert.Button";

const Alert = Object.assign(AlertRoot, {
  Title: AlertTitle,
  Description: AlertDescription,
  Actions: AlertActions,
  Button: AlertButton
});

export { Alert };
